// JP Compare Product Swiper Component
class JPProductSwiper extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.$section = null;
    this.currentSlide = 0;
    this.slides = [];
    this.pagination = null;
  }

  connectedCallback() {
    // Use native DOM methods instead of jQuery
    this.initSwiper();
  }

  disconnectedCallback() {
    this.destroySwiper();
  }

  initSwiper() {
    try {
      const container = this.querySelector('.jp-swiper-container');
      const wrapper = this.querySelector('.jp-swiper-wrapper');
      const slides = this.querySelectorAll('.jp-swiper-slide');
      this.pagination = this.querySelector('.jp-swiper-pagination');

      if (!container || !wrapper || slides.length === 0) {
        console.warn('JP Product Swiper: Required elements not found');
        return;
      }

      this.slides = Array.from(slides);

      // Initialize slides visibility
      this.slides.forEach((slide, index) => {
        slide.style.display = index === 0 ? 'flex' : 'none';
      });

      // Create pagination dots if multiple slides
      if (this.slides.length > 1 && this.pagination) {
        this.createPagination();
      }

      // Add touch/swipe support
      this.addTouchSupport();

      // Initialize Swiper if available and we have multiple slides
      if (typeof Swiper !== 'undefined' && this.slides.length > 1) {
        this.initSwiperLibrary();
      }

    } catch (error) {
      console.warn('JP Product Swiper initialization error:', error);
    }
  }

  createPagination() {
    if (!this.pagination || this.slides.length <= 1) return;
    
    this.pagination.innerHTML = '';
    
    this.slides.forEach((_, index) => {
      const dot = document.createElement('div');
      dot.className = 'jp-swiper-dot';
      if (index === 0) dot.classList.add('active');
      dot.addEventListener('click', () => this.goToSlide(index));
      this.pagination.appendChild(dot);
    });
  }

  addTouchSupport() {
    const wrapper = this.querySelector('.jp-swiper-wrapper');
    if (!wrapper || this.slides.length <= 1) return;

    let startX = 0;
    let startY = 0;
    let isDragging = false;
    let hasMoved = false;

    // Mouse events
    wrapper.addEventListener('mousedown', (e) => {
      if (this.slides.length <= 1) return;
      e.preventDefault();
      startX = e.clientX;
      startY = e.clientY;
      isDragging = true;
      hasMoved = false;
      wrapper.style.cursor = 'grabbing';
    });

    wrapper.addEventListener('mousemove', (e) => {
      if (!isDragging || this.slides.length <= 1) return;
      e.preventDefault();
      
      const diffX = e.clientX - startX;
      const diffY = e.clientY - startY;
      
      // Only trigger if horizontal movement is greater than vertical
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
        hasMoved = true;
      }
    });

    wrapper.addEventListener('mouseup', (e) => {
      if (!isDragging || this.slides.length <= 1) return;
      isDragging = false;
      wrapper.style.cursor = 'grab';
      
      if (hasMoved) {
        const diffX = e.clientX - startX;
        if (Math.abs(diffX) > 50) {
          if (diffX > 0) {
            this.previousSlide();
          } else {
            this.nextSlide();
          }
        }
      }
    });

    // Touch events
    wrapper.addEventListener('touchstart', (e) => {
      if (this.slides.length <= 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
      hasMoved = false;
    }, { passive: true });

    wrapper.addEventListener('touchmove', (e) => {
      if (!isDragging || this.slides.length <= 1) return;
      
      const diffX = e.touches[0].clientX - startX;
      const diffY = e.touches[0].clientY - startY;
      
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
        hasMoved = true;
        e.preventDefault();
      }
    }, { passive: false });

    wrapper.addEventListener('touchend', (e) => {
      if (!isDragging || this.slides.length <= 1) return;
      isDragging = false;
      
      if (hasMoved) {
        const diffX = e.changedTouches[0].clientX - startX;
        if (Math.abs(diffX) > 50) {
          if (diffX > 0) {
            this.previousSlide();
          } else {
            this.nextSlide();
          }
        }
      }
    }, { passive: true });

    // Add cursor style
    if (this.slides.length > 1) {
      wrapper.style.cursor = 'grab';
    }
  }

  initSwiperLibrary() {
    try {
      if (this.swiper) {
        this.swiper.destroy(true, true);
      }

      const container = this.querySelector('.jp-swiper-container');
      if (!container) {
        console.warn('JP Product Swiper: Container not found');
        return;
      }

      // Only initialize Swiper if we have multiple slides
      if (this.slides.length <= 1) {
        console.log('JP Product Swiper: Only one slide, skipping Swiper library initialization');
        return;
      }

      this.swiper = new Swiper(container, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: this.slides.length > 1,
        autoplay: false,
        pagination: {
          el: this.pagination,
          clickable: true,
          bulletClass: 'jp-swiper-dot',
          bulletActiveClass: 'active'
        },
        on: {
          slideChange: () => {
            this.currentSlide = this.swiper.realIndex;
            this.updatePagination();
          }
        }
      });

      // Sync pagination with swiper
      if (this.pagination) {
        this.pagination.addEventListener('click', (e) => {
          if (e.target.classList.contains('jp-swiper-dot')) {
            const index = Array.from(this.pagination.children).indexOf(e.target);
            if (this.swiper) {
              this.swiper.slideToLoop(index);
            }
          }
        });
      }

    } catch (error) {
      console.warn('JP Product Swiper library initialization error:', error);
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    this.slides.forEach((slide, i) => {
      slide.style.display = i === index ? 'flex' : 'none';
    });

    this.currentSlide = index;
    this.updatePagination();

    // Update swiper if available
    if (this.swiper) {
      this.swiper.slideToLoop(index);
    }
  }

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  previousSlide() {
    const prevIndex = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
    this.goToSlide(prevIndex);
  }

  updatePagination() {
    if (!this.pagination) return;

    const dots = this.pagination.querySelectorAll('.jp-swiper-dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentSlide);
    });
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

// Register the custom element
customElements.define('jp-product-swiper', JPProductSwiper);

// Mobile-specific swiper component
class JPMobileProductSwiper extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.currentSlide = 0;
    this.slides = [];
    this.pagination = null;
  }

  connectedCallback() {
    this.initSwiper();
  }

  disconnectedCallback() {
    this.destroySwiper();
  }

  initSwiper() {
    try {
      const container = this.querySelector('.jp-mobile-swiper-container');
      const wrapper = this.querySelector('.jp-mobile-swiper-wrapper');
      const slides = this.querySelectorAll('.jp-mobile-swiper-slide');
      this.pagination = this.querySelector('.jp-swiper-pagination');

      if (!container || !wrapper || slides.length === 0) {
        console.warn('JP Mobile Product Swiper: Required elements not found');
        return;
      }

      this.slides = Array.from(slides);

      // Initialize slides visibility
      this.slides.forEach((slide, index) => {
        slide.style.display = index === 0 ? 'flex' : 'none';
      });

      // Create pagination dots if multiple slides
      if (this.slides.length > 1 && this.pagination) {
        this.createPagination();
      }

      // Add touch/swipe support
      this.addTouchSupport();

      // Initialize Swiper if available and we have multiple slides
      if (typeof Swiper !== 'undefined' && this.slides.length > 1) {
        this.initSwiperLibrary();
      } else if (this.slides.length <= 1) {
        console.log('JP Mobile Product Swiper: Only one slide, skipping Swiper library initialization');
      }

    } catch (error) {
      console.warn('JP Mobile Product Swiper initialization error:', error);
    }
  }

  createPagination() {
    if (!this.pagination || this.slides.length <= 1) return;
    
    this.pagination.innerHTML = '';
    
    this.slides.forEach((_, index) => {
      const dot = document.createElement('div');
      dot.className = 'jp-swiper-dot';
      if (index === 0) dot.classList.add('active');
      dot.addEventListener('click', () => this.goToSlide(index));
      this.pagination.appendChild(dot);
    });
  }

  addTouchSupport() {
    const wrapper = this.querySelector('.jp-mobile-swiper-wrapper');
    if (!wrapper || this.slides.length <= 1) return;

    let startX = 0;
    let startY = 0;
    let isDragging = false;
    let hasMoved = false;

    // Touch events for mobile
    wrapper.addEventListener('touchstart', (e) => {
      if (this.slides.length <= 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
      hasMoved = false;
    }, { passive: true });

    wrapper.addEventListener('touchmove', (e) => {
      if (!isDragging || this.slides.length <= 1) return;
      
      const diffX = e.touches[0].clientX - startX;
      const diffY = e.touches[0].clientY - startY;
      
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
        hasMoved = true;
        e.preventDefault();
      }
    }, { passive: false });

    wrapper.addEventListener('touchend', (e) => {
      if (!isDragging || this.slides.length <= 1) return;
      isDragging = false;
      
      if (hasMoved) {
        const diffX = e.changedTouches[0].clientX - startX;
        if (Math.abs(diffX) > 50) {
          if (diffX > 0) {
            this.previousSlide();
          } else {
            this.nextSlide();
          }
        }
      }
    }, { passive: true });
  }

  initSwiperLibrary() {
    try {
      if (this.swiper) {
        this.swiper.destroy(true, true);
      }

      const container = this.querySelector('.jp-mobile-swiper-container');
      if (!container) {
        console.warn('JP Mobile Product Swiper: Container not found');
        return;
      }

      // Only initialize Swiper if we have multiple slides
      if (this.slides.length <= 1) {
        console.log('JP Mobile Product Swiper: Only one slide, skipping Swiper library initialization');
        return;
      }

      this.swiper = new Swiper(container, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: this.slides.length > 1,
        autoplay: false,
        pagination: {
          el: this.pagination,
          clickable: true,
          bulletClass: 'jp-swiper-dot',
          bulletActiveClass: 'active'
        },
        touchRatio: 1,
        threshold: 5,
        touchStartPreventDefault: false,
        on: {
          slideChange: () => {
            this.currentSlide = this.swiper.realIndex;
            this.updatePagination();
          }
        }
      });

      // Sync pagination with swiper
      if (this.pagination) {
        this.pagination.addEventListener('click', (e) => {
          if (e.target.classList.contains('jp-swiper-dot')) {
            const index = Array.from(this.pagination.children).indexOf(e.target);
            if (this.swiper) {
              this.swiper.slideToLoop(index);
            }
          }
        });
      }

    } catch (error) {
      console.warn('JP Mobile Product Swiper library initialization error:', error);
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    this.slides.forEach((slide, i) => {
      slide.style.display = i === index ? 'flex' : 'none';
    });

    this.currentSlide = index;
    this.updatePagination();

    // Update swiper if available
    if (this.swiper) {
      this.swiper.slideToLoop(index);
    }
  }

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  previousSlide() {
    const prevIndex = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
    this.goToSlide(prevIndex);
  }

  updatePagination() {
    if (!this.pagination) return;

    const dots = this.pagination.querySelectorAll('.jp-swiper-dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentSlide);
    });
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

// Register mobile swiper element
customElements.define('jp-mobile-product-swiper', JPMobileProductSwiper);
